import { cn } from '@distribute/frontend/utils';

import { Emoji } from 'emoji-picker-react';
import { TemplateExtended } from '@distribute/shared/api-types/templates';
import { Icon, Button, Dropdown, ButtonSquare } from '../../../shared/ui';
import { IconMap } from '../../../shared/sprite';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { FC } from 'react';
import { templateBgStyles } from '../../../features/templates/lib';
import { TemplatePreviewRaw } from '../../../features/templates/ui/components/TemplatePreviewRaw';

import { useTemplateActions, useTemplatesActions } from '../hooks';
import { CreatedBy } from '../../../entities/user';

type Props = {
  template: TemplateExtended;
  handleOpenDeleteTemplateModal: (template: TemplateExtended) => void;
  handleOpenTemplateSettingsModal: (template: TemplateExtended) => void;
};

export const TemplateCard: FC<Props> = ({
  template,
  handleOpenDeleteTemplateModal,
  handleOpenTemplateSettingsModal,
}) => {
  const {
    handleTemplateEdit,
    handleTemplatePreview,
    handleTemplateSelect,
    isDraft,
    templateOptions,
  } = useTemplateActions({
    template,
    handleOpenDeleteTemplateModal,
    handleOpenTemplateSettingsModal,
  });

  const { createPageIsLoading } = useTemplatesActions();

  return (
    <div className="flex flex-col gap-y-3 p-3 border border-gray-300 cursor-pointer rounded-xl group hover:shadow-md h-64">
      <div className="relative h-41 border border-base-black/[0.06] rounded-xl overflow-hidden">
        <div className="absolute z-10 flex flex-col items-center justify-center w-full h-full gap-3 transition-all duration-100 ease-linear opacity-0 group-hover:opacity-100 sm:hidden">
          {isDraft ? (
            <Button
              variant="text"
              color="primary"
              className="w-45 !h-11 text-md !font-semibold"
              onClick={handleTemplateEdit}
            >
              <Icon glyph={IconMap.Edit02} width={20} className="mr-1" />
              Edit draft
            </Button>
          ) : (
            <>
              <Button
                variant="text"
                color="primary"
                className="w-45 !h-11 text-md !font-semibold"
                onClick={handleTemplateSelect}
                disabled={createPageIsLoading}
              >
                Use Template
              </Button>

              <Button
                variant="text"
                color="secondary"
                className="w-45 !h-9 text-sm !font-semibold"
                onClick={() => handleTemplatePreview(template)}
              >
                <Icon glyph={IconMap.Eye} width={20} className="mr-1" />
                Preview
              </Button>
            </>
          )}
        </div>
        <div
          className={cn(
            'w-full h-full px-6 pt-5 group-hover:blur sm:group-hover:blur-none flex justify-center',
            `${templateBgStyles[template.bgColor].bgColor}`,
            {
              'justify-center': template.isSinglePage,
              'justify-between gap-3': !template.isSinglePage,
            }
          )}
        >
          <div
            className={cn(
              'w-8 h-[calc(100%-16px)] self-end grow -ml-6 shadow-md bg-base-white/60 rounded-tr-xl',
              { hidden: template.isSinglePage }
            )}
          ></div>
          <div className="relative w-full h-full shadow-md bg-base-white rounded-t-xl max-w-[216px] min-w-[216px]">
            <TemplatePreviewRaw
              style={{
                transform: `scale(0.22, 0.22)`,
                transformOrigin: '0 0',
              }}
              template={template}
              className="!pt-0 absolute w-212 left-4 md:left-5 top-4 md:top-5 h-full !overflow-visible"
            />
          </div>
          <div
            className={cn(
              'w-8 h-[calc(100%-16px)] self-end grow -mr-6 shadow-md bg-base-white/60 rounded-tl-xl',
              { hidden: template.isSinglePage }
            )}
          ></div>
        </div>
      </div>
      <div className="flex flex-nowrap items-center gap-x-2">
        <Emoji unified={template.content.icon} size={16} />
        <p className="text-sm font-semibold text-gray-900 truncate">
          {template.name || 'Untitled'}
        </p>
      </div>
      <div className="flex flex-nowrap gap-x-3 items-center justify-between">
        <CreatedBy
          displayName={template.owner.displayName}
          photoUrl={template.owner.photoUrl}
        />
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            side="bottom"
            align="start"
            listStyles="shadow-lg w-45"
            triggerComponent={
              <DropdownMenu.Trigger asChild>
                <ButtonSquare
                  size="sm"
                  variant="tertiary-gray"
                  className="focus:outline-none"
                >
                  <Icon glyph={IconMap.DotsVertical} width={20} />
                </ButtonSquare>
              </DropdownMenu.Trigger>
            }
            items={templateOptions}
          />
        </div>
      </div>
    </div>
  );
};
